import React, { createContext, useContext, useReducer, useMemo, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useParams, useLocation } from '@fs/zion-router'
import { useUser } from '@fs/zion-user'
import { usePerson } from '@fs/zion-person-service'
import { useTopicContext } from './TopicProvider'
import activityGroupReducer, { ACTIONS as A } from '../components/activityGroupReducer'
import {
  useGetPreviousAnswerGroups,
  useGetSecondaryPreviousAnswerGroups,
  useGetActivities,
  useSaveAnswers,
  useDeleteAnswerGroup,
  useMarkAsComplete,
  useSetGrouping,
} from '../hooks/api'
import { getURLParams } from '../helpers/url_helper'
import { useFamilyGroupContext } from './FamilyGroupProvider'
import topics from '../components/topics'
import { useGetActivityGroup, useRelevantActivityGroupId } from '../hooks'
import { getEffectiveActivityGroupId } from '../helpers/activityGroupIdHelper'
import useIsComponentMounted from '../hooks/useIsComponentMounted'
import useAnswerGroupById from '../hooks/useAnswerGroupById'
import useActivityGroupDerivations from '../hooks/useActivityGroupDerivations'
import useActivityGroupActions from '../hooks/useActivityGroupActions'
import useActivityAnswerFetchGate from '../hooks/useActivityAnswerFetchGate'
import useAnswerFetchConfig from '../hooks/useAnswerFetchConfig'
import useActivityGroupRouteSync from '../hooks/useActivityGroupRouteSync'
import useActivityGroupUrlRealign from '../hooks/useActivityGroupUrlRealign'

export const ActivityGroupStateContext = createContext()
export const ActivityGroupActionsContext = createContext()
export const useActivityGroupState = () => useContext(ActivityGroupStateContext)
export const useActivityGroupActionsContext = () => useContext(ActivityGroupActionsContext)

const INITIAL_ACTIVITY_GROUP_STATE = Object.freeze({
  isActivityGroupOpen: false,
  activeIndex: 0,
  activities: [],
  activityGroupAnswerId: null,
  requestedAnswerOwnerCisId: null,
  requestedActivityGroupId: null,
  activityGroupAnswers: null,
  activityGroupMode: '',
  memoryArtifact: null,
})

export default function ActivityGroupProvider({ children }) {
  const isComponentMountedRef = useIsComponentMounted()
  const l = useLocation()
  const [state, dispatch] = useReducer(activityGroupReducer, INITIAL_ACTIVITY_GROUP_STATE)
  const { topicId } = useTopicContext()
  const {
    activityGroupId: relevantActivityGroupId,
    subActivityGroupId,
    rootActivityGroupId,
    setCustomActivityGroupId,
  } = useRelevantActivityGroupId()
  const routeHasExplicitId = !!(subActivityGroupId || rootActivityGroupId)
  const queryClient = useQueryClient()
  const personId = getURLParams(l?.search)?.personId
  const person = usePerson(personId, 'card', null, false)
  const { signedIn, cisId: activeCisId } = useUser()
  const { cisId, answerId } = useParams()
  const parsedAnswerId = answerId ? parseInt(answerId, 10) : null

  // Effective id for group + activities fetches (feed-open aware)
  const effectiveActivityGroupIdForFetch = getEffectiveActivityGroupId({
    requested: state.requestedActivityGroupId,
    route: relevantActivityGroupId,
  })

  const { data: activityGroupData } = useGetActivityGroup({
    activityGroupId: effectiveActivityGroupIdForFetch,
    enabled: signedIn && !!effectiveActivityGroupIdForFetch,
  })
  const { data: activitiesData } = useGetActivities(
    effectiveActivityGroupIdForFetch,
    signedIn && !!effectiveActivityGroupIdForFetch
  )
  // (Removed temporary debug logging for activities query)

  const { isValidParsedAnswerId, effectiveActivityGroupId, effectiveActivityGroupAnswerId, cisUserForQuery } =
    useAnswerFetchConfig({ state, parsedAnswerId, cisId, activeCisId, relevantActivityGroupId })
  const { enabled: fetchAnswerEnabled, reason: fetchAnswerReason } = useActivityAnswerFetchGate({
    state,
    infoDataType: activityGroupData?.infoDataType,
    parsedAnswerId,
    signedIn,
  })

  const {
    refetch: refetchAnswerGroup,
    answers: pastAnswersData,
    isLoading: answersLoading,
  } = useAnswerGroupById({
    activityGroupId: effectiveActivityGroupId,
    activityGroupAnswerId: effectiveActivityGroupAnswerId,
    cisUserId: cisUserForQuery,
    enabled: fetchAnswerEnabled,
  })

  const {
    activityGroupEnriched,
    activitiesComputed,
    activityGroupModeDerived,
    activityGroupAnswerIdDerived,
    activityGroupAnswersDerived,
    selectedActivitiesWithAnswersDerived,
    memoryArtifactDerived,
  } = useActivityGroupDerivations({ activityGroupData, person, activitiesData, state, pastAnswersData })

  // Keep requestedActivityGroupId aligned with the current route id when user navigates directly.
  useEffect(() => {
    // Only sync when a real route param is present (avoid overlay feed opens being overwritten)
    if (routeHasExplicitId && relevantActivityGroupId && state.requestedActivityGroupId !== relevantActivityGroupId) {
      // Simple setter expects the property at the top level (NOT nested in value)
      dispatch({
        actionType: A.SET_REQUESTED_ACTIVITY_GROUP_ID,
        requestedActivityGroupId: relevantActivityGroupId,
      })
    }
  }, [routeHasExplicitId, relevantActivityGroupId, state.requestedActivityGroupId])

  const { mutation: saveAnswersMutation } = useSaveAnswers(relevantActivityGroupId)
  const { mutation: markAsCompleteMutation } = useMarkAsComplete()
  const { mutation: setGroupingMutation } = useSetGrouping()
  const { mutation: deleteAnswerGroupMutation } = useDeleteAnswerGroup()

  const getPreviousAnswersEnabled = !!(
    signedIn &&
    relevantActivityGroupId &&
    !cisId &&
    !answerId &&
    !state.isActivityGroupOpen &&
    (topicId === topics.stories.type || topicId === topics.temple.type)
  )
  const getSecondaryPreviousAnswersEnabled = getPreviousAnswersEnabled && relevantActivityGroupId === 'bucket-list'
  const { query: previousAnswerGroupsQuery, data: prevAnswerGroupsResData } = useGetPreviousAnswerGroups(
    relevantActivityGroupId,
    personId,
    getPreviousAnswersEnabled
  )
  const { data: prevSecondaryAnswerGroupsResData } = useGetSecondaryPreviousAnswerGroups(
    `${relevantActivityGroupId}-complete`,
    getSecondaryPreviousAnswersEnabled
  )
  const individualsAnswerGroupsDerived = prevAnswerGroupsResData?.answers ?? state?.individualsAnswerGroups
  const secondaryIndividualAnswerGroupsDerived =
    prevSecondaryAnswerGroupsResData?.answers ?? state?.secondaryIndividualAnswerGroups

  const { sharedAnswers, setSharedAnswers } = useFamilyGroupContext()

  const {
    saveAnswer,
    finishAnswers,
    openPreviousAnswers,
    clearAnswer,
    deleteAnswer,
    deleteAnswers,
    setAnswerGrouping,
    saveQueueStatus,
    saveQueueBusy,
    clearSaveQueue,
  } = useActivityGroupActions({
    state,
    dispatch,
    relevantActivityGroupId,
    activityGroupAnswerIdDerived,
    activityGroupAnswersDerived,
    queryClient,
    cisId,
    activeCisId,
    setCustomActivityGroupId,
    sharedAnswers,
    setSharedAnswers,
    saveAnswersMutation,
    markAsCompleteMutation,
    setGroupingMutation,
    deleteAnswerGroupMutation,
    isComponentMountedRef,
    routeHasExplicitId,
  })

  // (Deep link auto-open logic lives inside useActivityGroupRouteSync now; removed duplicate effect.)

  useActivityGroupRouteSync({
    isComponentMountedRef,
    locationPathname: l?.pathname || '',
    answerId,
    stateIsOpen: state.isActivityGroupOpen,
    dispatch,
    openPreviousAnswers,
    parsedAnswerId,
    relevantActivityGroupId,
    activitiesLength: activitiesData?.items?.length || 0,
    cisId,
    activeCisId,
    sharedAnswers,
    activityGroupAnswerIdDerived,
    isValidParsedAnswerId,
  })

  // Keep the URL in agreement with the UI when an INPUT session was started from another person's
  // answer (DSC-9347): once the user's own answer exists, realign the route to it.
  useActivityGroupUrlRealign({
    isOpen: state.isActivityGroupOpen,
    mode: activityGroupModeDerived,
    topicId,
    activityGroupId: relevantActivityGroupId,
    subActivityGroupId,
    ownAnswerId: state.activityGroupAnswerId,
    routeAnswerId: parsedAnswerId,
    activeCisId,
    pathname: l?.pathname,
  })

  const {
    isActivityGroupOpen,
    activeIndex,
    activityGroupAnswerId: activityGroupAnswerIdState,
    requestedAnswerOwnerCisId,
    requestedActivityGroupId,
    activityGroupAnswers: activityGroupAnswersState,
    activityGroupMode: activityGroupModeState,
    memoryArtifact: memoryArtifactState,
  } = state

  const contextValue = useMemo(
    () => ({
      // primitives / direct state values
      isActivityGroupOpen,
      activeIndex,
      requestedAnswerOwnerCisId,
      requestedActivityGroupId,
      dispatch,
      // derived overrides
      activityGroup: activityGroupEnriched,
      activities: activitiesComputed,
      individualsAnswerGroups: individualsAnswerGroupsDerived,
      secondaryIndividualAnswerGroups: secondaryIndividualAnswerGroupsDerived,
      activityGroupMode: activityGroupModeDerived ?? activityGroupModeState,
      activityGroupAnswerId: activityGroupAnswerIdDerived ?? activityGroupAnswerIdState,
      activityGroupAnswers: activityGroupAnswersDerived ?? activityGroupAnswersState,
      selectedActivitiesWithAnswers: selectedActivitiesWithAnswersDerived,
      memoryArtifact: memoryArtifactDerived ?? memoryArtifactState,
      answersLoading,
      activityGroupId: effectiveActivityGroupIdForFetch,
      subActivityGroupId,
      parentActivityGroupId: subActivityGroupId ? rootActivityGroupId : null,
      previousAnswerGroupsQuery,
      fetchAnswerEnabled,
      fetchAnswerReason,
    }),
    [
      isActivityGroupOpen,
      activeIndex,
      requestedAnswerOwnerCisId,
      requestedActivityGroupId,
      dispatch,
      activityGroupEnriched,
      activitiesComputed,
      individualsAnswerGroupsDerived,
      secondaryIndividualAnswerGroupsDerived,
      activityGroupModeDerived,
      activityGroupModeState,
      activityGroupAnswerIdDerived,
      activityGroupAnswerIdState,
      activityGroupAnswersDerived,
      activityGroupAnswersState,
      selectedActivitiesWithAnswersDerived,
      memoryArtifactDerived,
      memoryArtifactState,
      answersLoading,
      effectiveActivityGroupIdForFetch,
      subActivityGroupId,
      rootActivityGroupId,
      previousAnswerGroupsQuery,
      fetchAnswerEnabled,
      fetchAnswerReason,
    ]
  )

  const actionsValue = useMemo(
    () => ({
      saveAnswer,
      finishAnswers,
      openPreviousAnswers,
      clearAnswer,
      deleteAnswer,
      deleteAnswers,
      setAnswerGrouping,
      setCustomActivityGroupId,
      saveQueueStatus,
      saveQueueBusy,
      clearSaveQueue,
      // legacy naming retained for existing component props/tests expecting this
      loadPastAnswers: refetchAnswerGroup,
    }),
    [
      saveAnswer,
      finishAnswers,
      openPreviousAnswers,
      clearAnswer,
      deleteAnswer,
      deleteAnswers,
      setAnswerGrouping,
      setCustomActivityGroupId,
      saveQueueStatus,
      saveQueueBusy,
      clearSaveQueue,
      refetchAnswerGroup,
    ]
  )

  return (
    <ActivityGroupStateContext.Provider value={contextValue}>
      <ActivityGroupActionsContext.Provider value={actionsValue}>{children}</ActivityGroupActionsContext.Provider>
    </ActivityGroupStateContext.Provider>
  )
}
